import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertActionLink,
  Bullseye,
  Button,
  Checkbox,
  Content,
  Form,
  FormGroup,
  FormSelect,
  FormSelectOption,
  MenuToggle,
  MenuToggleElement,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Select,
  SelectList,
  SelectOption,
  Spinner,
} from '@patternfly/react-core';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useDnsEndpoints } from '../../../hooks/useDnsEndpoints';
import { isApexName } from '../Domains/zoneRecords';
import type { AntispamPlatform, AntispamPlatformMxRecord } from '../../../types/models/AntispamPlatform';
import type { MailDomainDnsZoneState } from '../../../types/models/MailDomain';
import type { Zone, ZoneRecord } from '../../../types/models/Zone';
import type {
  ApiResponseAntispamPlatformMxRecord,
  ApiResponseMailDomainAntispamMutation,
} from '../../../types/api/ApiResponseAntispamPlatform';
import type { ApiResponseMailDomainDnsZone } from '../../../types/api/ApiResponseMailDomain';
import type { ApiResponseActivateDnsZone, ApiResponseDnsRrsetWrite } from '../../../types/api/ApiResponseDomainName';

interface EnableAntispamModalProps {
  domainId: number; // the mail_domain id
  organizationDomainId: number; // bridge to the DNS side (dns-zone route is keyed by this)
  platforms: AntispamPlatform[];
  isOpen: boolean;
  onClose: () => void;
  /** Refetch the parent so the antispam status reflects reality. The modal owns user notifications. */
  onSuccess: () => void;
}

/**
 * The DNS action the modal can offer alongside enabling antispam, derived from the zone state:
 *  - `configured` — zone exists; just (re)write the apex MX.
 *  - `activate`    — registered with us but unbound; activate the zone on a chosen endpoint, then MX.
 *  - `repair`      — bound but the zone is missing (desync); repair, then MX.
 *  - `unmanaged`   — domain isn't in our DNS module; manual MX only.
 *  - `unknown`     — bound but the endpoint couldn't be probed; manual MX only, offer a retry.
 */
type DnsActionKind = 'configured' | 'activate' | 'repair' | 'unmanaged' | 'unknown';

function deriveDnsActionKind(dz: MailDomainDnsZoneState): DnsActionKind {
  if (dz.zone_state === 'configured') {
    return 'configured';
  }
  if (dz.zone_state === 'unknown') {
    return 'unknown';
  }
  // not_configured — distinguish the three sub-cases by (domain_name_id, dns_endpoint_id).
  if (dz.domain_name_id === null) {
    return 'unmanaged';
  }
  return dz.dns_endpoint_id === null ? 'activate' : 'repair';
}

/** Kinds where we can write the apex MX (configured/activate/repair); unmanaged/unknown are manual-only. */
function isDnsWritingKind(kind: DnsActionKind | null): kind is 'configured' | 'activate' | 'repair' {
  return kind === 'configured' || kind === 'activate' || kind === 'repair';
}

export const EnableAntispamModal = ({
  domainId,
  organizationDomainId,
  platforms,
  isOpen,
  onClose,
  onSuccess,
}: EnableAntispamModalProps) => {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();

  // Platform selection (unchanged behaviour from the original modal).
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  // DNS zone state for the domain (fetched on open / retry).
  const [dnsZone, setDnsZone] = useState<MailDomainDnsZoneState | null>(null);
  const [dnsZoneLoading, setDnsZoneLoading] = useState(false);
  const [dnsZoneError, setDnsZoneError] = useState<string | null>(null);

  // MX target for the selected platform (refetched whenever the platform changes).
  const [mxRecord, setMxRecord] = useState<AntispamPlatformMxRecord | null>(null);
  const [mxLoading, setMxLoading] = useState(false);
  const [mxError, setMxError] = useState<string | null>(null);

  // DNS action opt-in (default on) + endpoint picker for the activate sub-case.
  const [pointMxAtGateway, setPointMxAtGateway] = useState(true);
  const [selectedEndpointId, setSelectedEndpointId] = useState<string>('');

  const [error, setError] = useState<string | null>(null); // step-1 (enable) failure → inline danger
  const [warning, setWarning] = useState<string | null>(null); // partial failure after antispam enabled
  const [antispamEnabled, setAntispamEnabled] = useState(false); // step-1 succeeded (drives the partial-failure footer)
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedPlatform = platforms.find((p) => p.id === selectedId) ?? null;
  const dnsKind = dnsZone ? deriveDnsActionKind(dnsZone) : null;
  const needsEndpointPicker = dnsKind === 'activate';

  // The endpoint catalog is only needed for the activate sub-case; the hook fetches when this flips true.
  const {
    endpoints,
    loading: endpointsLoading,
    error: endpointsError,
    refetch: refetchEndpoints,
  } = useDnsEndpoints(isOpen && needsEndpointPicker);

  const fetchDnsZone = useCallback(async () => {
    setDnsZoneLoading(true);
    setDnsZoneError(null);
    try {
      const response = await apiFetch(`/emails/domains/dns-zone?organization_domain_id=${organizationDomainId}`);
      const body = (await response.json().catch(() => null)) as ApiResponseMailDomainDnsZone | null;
      if (!response.ok || body?.status !== 'success' || !body.data?.dns_zone) {
        setDnsZone(null);
        setDnsZoneError(body?.message ?? `Request failed with status ${response.status}`);
        return;
      }
      setDnsZone(body.data.dns_zone);
    } catch (err) {
      setDnsZone(null);
      setDnsZoneError(err instanceof Error ? err.message : 'An unexpected error occurred while checking the DNS zone.');
    } finally {
      setDnsZoneLoading(false);
    }
  }, [apiFetch, organizationDomainId]);

  const fetchMxRecord = useCallback(
    async (platformId: number) => {
      setMxLoading(true);
      setMxError(null);
      try {
        const response = await apiFetch(`/emails/antispam-platforms/${platformId}/mx-record`);
        const body = (await response.json().catch(() => null)) as ApiResponseAntispamPlatformMxRecord | null;
        if (!response.ok || body?.status !== 'success' || !body.data?.mx_record) {
          setMxRecord(null);
          setMxError(body?.message ?? `Request failed with status ${response.status}`);
          return;
        }
        setMxRecord(body.data.mx_record);
      } catch (err) {
        setMxRecord(null);
        setMxError(
          err instanceof Error ? err.message : 'An unexpected error occurred while resolving the antispam MX target.',
        );
      } finally {
        setMxLoading(false);
      }
    },
    [apiFetch],
  );

  // Reset everything on each open and kick off the zone-state fetch; pre-select a lone platform.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setSelectedId(platforms.length === 1 ? platforms[0].id : null);
    setIsSelectOpen(false);
    setDnsZone(null);
    setDnsZoneError(null);
    setMxRecord(null);
    setMxError(null);
    setPointMxAtGateway(true);
    setSelectedEndpointId('');
    setError(null);
    setWarning(null);
    setAntispamEnabled(false);
    setIsSubmitting(false);
    void fetchDnsZone();
  }, [isOpen, platforms, fetchDnsZone]);

  // Fetch the MX target for the selected platform (and refetch when it changes).
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (selectedId === null) {
      setMxRecord(null);
      setMxError(null);
      return;
    }
    void fetchMxRecord(selectedId);
  }, [isOpen, selectedId, fetchMxRecord]);

  // We can configure DNS only on a writing kind once the MX target is known; opt-in via the checkbox.
  const canConfigureDns = isDnsWritingKind(dnsKind) && mxRecord !== null;
  const willConfigureDns = canConfigureDns && pointMxAtGateway;

  const enableDisabled =
    isSubmitting ||
    selectedId === null ||
    dnsZoneLoading || // wait until we know the zone state before committing
    (isDnsWritingKind(dnsKind) && pointMxAtGateway && mxLoading) || // wait for the MX target if we'll use it
    (willConfigureDns && needsEndpointPicker && selectedEndpointId === ''); // activate needs an endpoint

  // ── Enable sequence helpers ─────────────────────────────────────────────────

  // Run an activate / repair POST. Both return the ApiResponseActivateDnsZone shape; throw on failure.
  const runZoneStep = async (path: string, payload: Record<string, unknown> | null): Promise<Zone | null> => {
    const response = await apiFetch(path, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      ...(payload !== null ? { body: JSON.stringify(payload) } : {}),
    });
    const body = (await response.json().catch(() => null)) as ApiResponseActivateDnsZone | null;
    if (!response.ok || body?.status !== 'success' || body.data?.dns_endpoint_id == null) {
      throw new Error(body?.message ?? `Request failed with status ${response.status}`);
    }
    return body.data.zone;
  };

  // Create (POST) or replace (PATCH) the apex MX RRSet, deciding from the freshest zone records and
  // falling back POST→PATCH once on the backend's name-collision error.
  const writeApexMx = async (
    domainNameId: number,
    apex: string,
    mxContent: string,
    records: ZoneRecord[],
  ): Promise<void> => {
    const apexMxExists = records.some((r) => r.type.toUpperCase() === 'MX' && isApexName(r.name, apex));
    const payload = { name: apex, type: 'MX', ttl: 3600, content: [mxContent] };
    const send = (method: 'POST' | 'PATCH') =>
      apiFetch(`/datacenters/domain-names/${domainNameId}/dns/rrset`, {
        method,
        headers: { Accept: 'application/json' },
        body: JSON.stringify(payload),
      });

    let method: 'POST' | 'PATCH' = apexMxExists ? 'PATCH' : 'POST';
    let response = await send(method);
    let body = (await response.json().catch(() => null)) as ApiResponseDnsRrsetWrite | null;

    // Collision fallback: a POST that hit an existing set retries once as a PATCH (replace in place).
    const collided =
      method === 'POST' &&
      (!response.ok || body?.status !== 'success') &&
      typeof body?.data?.name === 'string' &&
      body.data.name.toLowerCase().includes('already exists');
    if (collided) {
      method = 'PATCH';
      response = await send('PATCH');
      body = (await response.json().catch(() => null)) as ApiResponseDnsRrsetWrite | null;
    }

    if (response.ok && body?.status === 'success' && body.data?.record_set) {
      return;
    }
    const reason =
      (typeof body?.data?.name === 'string' ? body.data.name : null) ??
      body?.message ??
      `Request failed with status ${response.status}`;
    throw new Error(reason);
  };

  // Antispam is on but a later (opt-in) DNS/MX step failed: refresh the parent, keep the modal open
  // with a warning + the manual remediation. We never roll antispam back.
  const handlePartialFailure = (stepLabel: string, err: unknown) => {
    const reason = err instanceof Error ? err.message : 'An unexpected error occurred.';
    const manual = mxRecord
      ? ` Add it manually at your DNS provider: priority ${mxRecord.priority} → ${mxRecord.host}.`
      : '';
    setWarning(`Antispam is enabled, but we couldn't ${stepLabel}: ${reason}.${manual}`);
    setIsSubmitting(false);
    onSuccess();
  };

  // Antispam enabled with no DNS write (unmanaged / unknown / opt-out / MX unresolved). For the cases
  // where DNS is out of our hands, append the manual MX so the user knows what to publish.
  const finishAntispamOnly = () => {
    const domain = dnsZone?.domain ?? 'this domain';
    let manual = '';
    if (mxRecord && dnsKind === 'unmanaged') {
      manual = ` This domain's DNS isn't managed by Ancalagon, so add its MX manually: priority ${mxRecord.priority} → ${mxRecord.host}.`;
    } else if (mxRecord && dnsKind === 'unknown') {
      manual = ` We couldn't verify this domain's DNS zone, so check or add its MX manually: priority ${mxRecord.priority} → ${mxRecord.host}.`;
    } else if (mxRecord && dnsKind === null) {
      // Zone state couldn't be determined (fetch error) — still tell the user what to publish.
      manual = ` Set its MX manually at your DNS provider: priority ${mxRecord.priority} → ${mxRecord.host}.`;
    }
    addNotification({
      title: 'Antispam enabled',
      srTitle: 'Success alert',
      description: `Antispam is now managing ${domain}.${manual}`,
      variant: 'success',
    });
    setDrawerOpen(true);
    onSuccess();
    onClose();
  };

  const finishSuccessWithDns = () => {
    const domain = dnsZone?.domain ?? 'this domain';
    addNotification({
      title: 'Antispam enabled',
      srTitle: 'Success alert',
      description: `Antispam is now managing ${domain}, and its apex MX now points to the antispam gateway${
        mxRecord ? ` (${mxRecord.content})` : ''
      }.`,
      variant: 'success',
    });
    setDrawerOpen(true);
    onSuccess();
    onClose();
  };

  const handleEnable = async () => {
    if (selectedId === null) {
      return;
    }
    setIsSubmitting(true);
    setError(null);
    setWarning(null);

    // ── Step 1: enable antispam (never rolled back once it succeeds) ──
    try {
      const response = await apiFetch(`/emails/domains/${domainId}/antispam-platform`, {
        method: 'PUT',
        headers: { Accept: 'application/json' },
        body: JSON.stringify({ antispam_platform_id: selectedId }),
      });
      const body = (await response.json().catch(() => null)) as ApiResponseMailDomainAntispamMutation | null;
      if (!response.ok || body?.status !== 'success' || !body.data?.mail_domain) {
        setError(body?.message ?? `Request failed with status ${response.status}`);
        setIsSubmitting(false);
        return;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected network error occurred.');
      setIsSubmitting(false);
      return;
    }
    setAntispamEnabled(true);

    // No DNS action chosen / possible → finish here.
    if (!willConfigureDns || dnsZone === null || mxRecord === null) {
      finishAntispamOnly();
      setIsSubmitting(false);
      return;
    }

    const domainNameId = dnsZone.domain_name_id;
    if (domainNameId === null) {
      // Defensive: writing kinds always carry a domain_name_id.
      finishAntispamOnly();
      setIsSubmitting(false);
      return;
    }

    // ── Step 2: ensure the zone exists (activate / repair); 'configured' needs no step ──
    let zoneRecords: ZoneRecord[] = dnsZone.zone?.records ?? [];
    try {
      if (dnsKind === 'activate') {
        const zone = await runZoneStep(`/datacenters/domain-names/${domainNameId}/zone`, {
          dns_endpoint_id: Number(selectedEndpointId),
        });
        zoneRecords = zone?.records ?? [];
      } else if (dnsKind === 'repair') {
        const zone = await runZoneStep(`/datacenters/domain-names/${domainNameId}/zone/repair`, null);
        zoneRecords = zone?.records ?? zoneRecords;
      }
    } catch (err) {
      handlePartialFailure(dnsKind === 'activate' ? 'activate the DNS zone' : 'repair the DNS zone', err);
      return;
    }

    // ── Step 3: write the apex MX RRSet ──
    try {
      await writeApexMx(domainNameId, dnsZone.domain, mxRecord.content, zoneRecords);
    } catch (err) {
      handlePartialFailure('set the MX record', err);
      return;
    }

    finishSuccessWithDns();
    setIsSubmitting(false);
  };

  // ── Render helpers ──────────────────────────────────────────────────────────

  const toggle = (toggleRef: React.Ref<MenuToggleElement>) => (
    <MenuToggle
      ref={toggleRef}
      onClick={() => setIsSelectOpen((open) => !open)}
      isExpanded={isSelectOpen}
      isDisabled={isSubmitting || platforms.length === 0}
      isFullWidth
      aria-label="Select antispam platform"
    >
      {selectedPlatform ? `${selectedPlatform.name} (${selectedPlatform.type_label})` : 'Select a platform'}
    </MenuToggle>
  );

  const renderEndpointPicker = () => (
    <FormGroup
      label="Nameserver endpoint"
      isRequired
      fieldId="enable-antispam-dns-endpoint"
      className="pf-v6-u-mt-sm"
    >
      {endpointsLoading ? (
        <Bullseye>
          <Spinner size="md" aria-label="Loading DNS endpoints" />
        </Bullseye>
      ) : endpointsError !== null ? (
        <Alert
          variant="danger"
          isInline
          title="Couldn't load DNS endpoints"
          actionLinks={<AlertActionLink onClick={() => void refetchEndpoints()}>Retry</AlertActionLink>}
        >
          {endpointsError}
        </Alert>
      ) : endpoints.length === 0 ? (
        <Alert variant="info" isInline isPlain title="No DNS endpoints available">
          No DNS endpoints are available to activate this zone.
        </Alert>
      ) : (
        <FormSelect
          id="enable-antispam-dns-endpoint"
          value={selectedEndpointId}
          onChange={(_event, value) => setSelectedEndpointId(value)}
          isDisabled={isSubmitting}
        >
          <FormSelectOption value="" label="Select an endpoint" isPlaceholder />
          {endpoints.map((endpoint) => (
            <FormSelectOption key={endpoint.id} value={String(endpoint.id)} label={endpoint.name} />
          ))}
        </FormSelect>
      )}
    </FormGroup>
  );

  const renderDnsSection = () => {
    if (dnsZoneLoading) {
      return (
        <Bullseye className="pf-v6-u-mt-md">
          <Spinner size="md" aria-label="Checking DNS zone" />
        </Bullseye>
      );
    }
    if (dnsZoneError !== null) {
      return (
        <Alert
          variant="danger"
          isInline
          title="Couldn't check the DNS zone"
          className="pf-v6-u-mt-md"
          actionLinks={<AlertActionLink onClick={() => void fetchDnsZone()}>Retry</AlertActionLink>}
        >
          {dnsZoneError} You can still enable antispam and set the MX record manually afterwards.
        </Alert>
      );
    }
    if (!dnsZone || dnsKind === null) {
      return null;
    }

    if (dnsKind === 'unknown') {
      return (
        <Alert
          variant="danger"
          isInline
          title="DNS zone state unknown"
          className="pf-v6-u-mt-md"
          actionLinks={<AlertActionLink onClick={() => void fetchDnsZone()}>Retry</AlertActionLink>}
        >
          {dnsZone.zone_error ?? 'The DNS zone could not be checked.'} Antispam can still be enabled; we won&apos;t
          change DNS.
          {mxRecord && (
            <>
              {' '}
              Set this MX manually: priority {mxRecord.priority} → <code>{mxRecord.host}</code>.
            </>
          )}
        </Alert>
      );
    }

    if (dnsKind === 'unmanaged') {
      return (
        <Alert variant="info" isInline isPlain title="DNS not managed by Ancalagon" className="pf-v6-u-mt-md">
          This domain&apos;s DNS isn&apos;t managed by Ancalagon, so we can&apos;t set its MX automatically. After
          enabling, add this MX record at your DNS provider:{' '}
          {mxRecord ? (
            <>
              priority {mxRecord.priority} → <code>{mxRecord.host}</code>
            </>
          ) : (
            'priority 10 → the antispam gateway'
          )}
          .
        </Alert>
      );
    }

    // configured / activate / repair → opt-in checkbox + (endpoint picker) + MX target.
    const checkboxLabel =
      dnsKind === 'configured'
        ? "Point this domain's MX at the antispam gateway"
        : dnsKind === 'activate'
          ? 'Activate the DNS zone and point its MX at the gateway'
          : 'Repair the DNS zone and point its MX at the gateway';

    const apexMxExists =
      dnsKind === 'configured' &&
      (dnsZone.zone?.records.some((r) => r.type.toUpperCase() === 'MX' && isApexName(r.name, dnsZone.domain)) ?? false);

    const description =
      selectedId === null
        ? 'Select an antispam platform to see the MX record that will be published.'
        : mxLoading
          ? 'Resolving the antispam MX target…'
          : !mxError && mxRecord
            ? `Sets the apex MX to ${mxRecord.content}.`
            : undefined;

    return (
      <Form className="pf-v6-u-mt-md">
        <FormGroup label="DNS" fieldId="enable-antispam-dns-action">
          <Checkbox
            id="enable-antispam-dns-action"
            label={checkboxLabel}
            description={description}
            isChecked={pointMxAtGateway}
            onChange={(_event, checked) => setPointMxAtGateway(checked)}
            isDisabled={isSubmitting}
            body={
              pointMxAtGateway ? (
                <>
                  {needsEndpointPicker && renderEndpointPicker()}
                  {mxError !== null && (
                    <Alert
                      variant="warning"
                      isInline
                      isPlain
                      title="Couldn't resolve the antispam MX target"
                      className="pf-v6-u-mt-sm"
                    >
                      {mxError} Antispam can still be enabled; the MX won&apos;t be set automatically.
                    </Alert>
                  )}
                  {mxError === null && mxRecord && apexMxExists && (
                    <Content component="small" className="pf-v6-u-mt-sm">
                      An apex MX record already exists and will be replaced.
                    </Content>
                  )}
                </>
              ) : null
            }
          />
        </FormGroup>
      </Form>
    );
  };

  return (
    <Modal
      variant="medium"
      isOpen={isOpen}
      onClose={onClose}
      onEscapePress={() => (isSelectOpen ? setIsSelectOpen(false) : onClose())}
      aria-label="Enable antispam"
    >
      <ModalHeader title="Enable antispam" />
      <ModalBody>
        {warning !== null ? (
          <Alert variant="warning" isInline title="Antispam enabled — DNS not fully configured">
            {warning}
          </Alert>
        ) : (
          <>
            {error !== null && (
              <Alert variant="danger" isInline title="Enable failed" className="pf-v6-u-mb-md">
                {error}
              </Alert>
            )}
            <Content className="pf-v6-u-mb-md">
              Choose the antispam platform to filter mail for this domain. Enabling provisions the domain on the
              platform — this can take a few seconds.
            </Content>
            <Select
              id="enable-antispam-platform-select"
              isOpen={isSelectOpen}
              selected={selectedId ?? undefined}
              onSelect={(_event, value) => {
                if (typeof value === 'number') {
                  setSelectedId(value);
                }
                setIsSelectOpen(false);
              }}
              onOpenChange={(open) => setIsSelectOpen(open)}
              toggle={toggle}
            >
              <SelectList>
                {platforms.length === 0 ? (
                  <SelectOption isAriaDisabled value="__none__">
                    No antispam platforms available
                  </SelectOption>
                ) : (
                  platforms.map((p) => (
                    <SelectOption key={p.id} value={p.id}>
                      {`${p.name} (${p.type_label})`}
                    </SelectOption>
                  ))
                )}
              </SelectList>
            </Select>
            {renderDnsSection()}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        {antispamEnabled && warning !== null ? (
          <Button variant="primary" onClick={onClose}>
            Close
          </Button>
        ) : (
          <>
            <Button
              variant="primary"
              onClick={() => void handleEnable()}
              isLoading={isSubmitting}
              isDisabled={enableDisabled}
            >
              Enable
            </Button>
            <Button variant="link" onClick={onClose} isDisabled={isSubmitting}>
              Cancel
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
};
